/** How long a wave may stay in flight before the next wave is released. */
export const DASHBOARD_FETCH_WAVE_TIMEOUT_MS = 12_000

export type DashboardFetchWavePhase = 'armed' | 'watching'

export type DashboardFetchWaveState = {
  released: number
  phase: DashboardFetchWavePhase
}

export function initialDashboardFetchWaveState(): DashboardFetchWaveState {
  return { released: 0, phase: 'armed' }
}

/**
 * One scheduler pass.
 * Armed only becomes watching, so the first idle frame after a wave opens
 * cannot skip ahead of fetches that start in that same commit.
 * Watching releases the next wave once nothing in the current wave is loading.
 */
export function stepDashboardFetchWave(
  state: DashboardFetchWaveState,
  pending: boolean,
  waveCount: number,
): DashboardFetchWaveState {
  if (waveCount < 1) return state
  if (state.released >= waveCount - 1 && state.phase === 'watching') {
    return state
  }
  if (state.phase === 'armed') {
    return { released: state.released, phase: 'watching' }
  }
  if (!pending && state.released < waveCount - 1) {
    return { released: state.released + 1, phase: 'armed' }
  }
  return state
}

/** Releases the next wave when the current one is still pending past the cap. */
export function timeoutDashboardFetchWave(
  state: DashboardFetchWaveState,
  waveCount: number,
): DashboardFetchWaveState {
  if (waveCount < 1 || state.released >= waveCount - 1) return state
  return { released: state.released + 1, phase: 'armed' }
}

export function isDashboardFetchWavePending(
  reports: ReadonlyArray<{ wave: number; loading: boolean }>,
  released: number,
): boolean {
  for (let index = 0; index < reports.length; index += 1) {
    const report = reports[index]
    if (report.wave === released && report.loading) return true
  }
  return false
}

/** Lowest non-null wave, or null when every entry is hidden. */
export function earliestWave(
  waves: ReadonlyArray<number | null | undefined>,
): number | null {
  let earliest: number | null = null
  for (let index = 0; index < waves.length; index += 1) {
    const wave = waves[index]
    if (wave == null) continue
    if (earliest == null || wave < earliest) earliest = wave
  }
  return earliest
}

/** Lowest wave among visible sections, or null when every section is hidden. */
export function earliestVisibleWave(
  entries: ReadonlyArray<readonly [boolean, number]>,
): number | null {
  let earliest: number | null = null
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index]
    if (!entry[0]) continue
    if (earliest == null || entry[1] < earliest) earliest = entry[1]
  }
  return earliest
}
